import type { FieldGroup } from './types.js';

/**
 * Step 8 — Mental Health & Neurodiversity (restructured 2026-04)
 *
 * 14 sub-steps covering:
 *   1.  neurodiverseIdentity (diagnosis status)
 *   2.  neurodevelopmentalConditions (core ND — conditional on ND identity)
 *   3.  mentalHealthConditions (mood, anxiety, trauma, OCD-spectrum, eating, psychotic — shown to everyone)
 *   4.  personalityPatterns (DSM-5 Cluster A/B/C — shown to everyone)
 *   5.  neurologicalChronicConditions (comorbid neurological/chronic — shown to everyone)
 *   6.  ageOfRealization
 *   7.  sensoryProfile
 *   8.  cognitiveStrengths
 *   9.  executiveFunctionChallenges
 *   10. learningStyle
 *   11. communicationPreference
 *   12. supportAndAccommodations
 *   13. maskingAndBurnout
 *   14. impactOnLife
 *
 * Condition lists are DSM-5-TR (2022) aligned with person-first, non-pathologizing
 * language. Each option has a full descriptive name (not just an acronym) and a
 * description explaining what it means — both translatable via i18n.
 */

// ─── 1. Identity ────────────────────────────────────────────────────────────

export const neurodiverseIdentityOptions = [
  {
    value: 'yes_diagnosed',
    label: 'Yes — formally diagnosed',
    description: 'Clinically diagnosed by a qualified professional',
  },
  {
    value: 'yes_self_identified',
    label: 'Yes — self-identified',
    description: 'Identify as neurodivergent without formal diagnosis',
  },
  {
    value: 'exploring',
    label: 'Exploring / suspect',
    description: "Not sure yet — exploring whether I'm neurodivergent",
  },
  {
    value: 'no_neurotypical',
    label: 'No — neurotypical',
    description: "Don't identify as neurodivergent",
  },
  { value: 'prefer_not_to_say', label: 'Prefer not to say' },
] as const;

// ─── 2. Neurodevelopmental Conditions (DSM-5 Neurodevelopmental Disorders) ──

export const neurodevelopmentalConditionOptions = [
  {
    value: 'adhd',
    label: 'ADHD — Attention Deficit Hyperactivity Disorder',
    description:
      'Inattentive, hyperactive, or combined type — affects focus, impulse control, and organization',
  },
  {
    value: 'autism',
    label: 'Autism Spectrum Condition (ASC / ASD)',
    description:
      'Differences in social communication, sensory processing, and patterns of behavior',
  },
  {
    value: 'dyslexia',
    label: 'Dyslexia — Reading & Language Processing',
    description:
      'Difficulty with reading, spelling, and language decoding despite adequate intelligence',
  },
  {
    value: 'dyscalculia',
    label: 'Dyscalculia — Mathematical Processing',
    description: 'Difficulty with numbers, arithmetic, and mathematical reasoning',
  },
  {
    value: 'dysgraphia',
    label: 'Dysgraphia — Writing & Fine Motor',
    description: 'Difficulty with handwriting, spelling, and organizing written thoughts',
  },
  {
    value: 'dyspraxia_dcd',
    label: 'Dyspraxia / DCD — Developmental Coordination Disorder',
    description: 'Difficulty with motor coordination, planning movements, and spatial awareness',
  },
  {
    value: 'tourettes_tics',
    label: "Tourette's Syndrome / Tic Disorder",
    description: 'Involuntary repetitive movements or vocalizations (motor and/or vocal tics)',
  },
  {
    value: 'speech_sound',
    label: 'Speech Sound Disorder',
    description: 'Difficulty producing speech sounds correctly — affects pronunciation and clarity',
  },
  {
    value: 'stuttering',
    label: 'Stuttering / Fluency Disorder',
    description: 'Disruptions in the flow of speech — repetitions, prolongations, or blocks',
  },
  {
    value: 'language_disorder',
    label: 'Language Disorder',
    description: 'Difficulty understanding or using spoken/written language despite normal hearing',
  },
  {
    value: 'social_communication',
    label: 'Social (Pragmatic) Communication Disorder',
    description:
      'Difficulty with social use of language — understanding context, humor, or implied meaning',
  },
  {
    value: 'intellectual_disability',
    label: 'Intellectual Disability',
    description: 'Limitations in intellectual functioning and adaptive behavior',
  },
  {
    value: 'sensory_processing',
    label: 'Sensory Processing Differences',
    description:
      'Difficulty processing and responding to sensory input (sound, light, touch, etc.)',
  },
  {
    value: 'giftedness_2e',
    label: 'Giftedness / Twice-Exceptional (2e)',
    description: 'High cognitive ability combined with a neurodevelopmental difference',
  },
  {
    value: 'other_neurodevelopmental',
    label: 'Other neurodevelopmental condition',
    description: 'A neurodevelopmental condition not listed above',
  },
  { value: 'prefer_not_to_say', label: 'Prefer not to say' },
] as const;

// ─── 3. Mental Health Conditions (DSM-5 mood, anxiety, trauma, OCD, eating, psychotic) ──

export const mentalHealthConditionOptions = [
  // Depressive disorders
  {
    value: 'depression_major',
    label: 'Major Depressive Disorder',
    description: 'Persistent sadness, loss of interest, and fatigue lasting 2+ weeks',
  },
  {
    value: 'depression_persistent',
    label: 'Persistent Depressive Disorder (Dysthymia)',
    description: 'Chronic low-grade depression lasting 2+ years',
  },
  {
    value: 'depression_seasonal',
    label: 'Seasonal Affective Disorder (SAD)',
    description: 'Depression that follows a seasonal pattern, typically in winter',
  },
  {
    value: 'pmdd',
    label: 'Premenstrual Dysphoric Disorder (PMDD)',
    description: 'Severe mood changes tied to the menstrual cycle',
  },
  // Bipolar & related
  {
    value: 'bipolar_i',
    label: 'Bipolar I Disorder',
    description: 'Manic episodes lasting 7+ days, often with depressive episodes',
  },
  {
    value: 'bipolar_ii',
    label: 'Bipolar II Disorder',
    description: 'Hypomanic and depressive episodes without full mania',
  },
  {
    value: 'cyclothymia',
    label: 'Cyclothymic Disorder',
    description: 'Chronic mood fluctuations — milder than bipolar but persistent',
  },
  // Anxiety disorders
  {
    value: 'anxiety_generalized',
    label: 'Generalized Anxiety Disorder (GAD)',
    description: 'Persistent, excessive worry about everyday situations',
  },
  {
    value: 'anxiety_social',
    label: 'Social Anxiety Disorder',
    description: 'Intense fear of social situations and being judged by others',
  },
  {
    value: 'panic_disorder',
    label: 'Panic Disorder',
    description: 'Recurrent unexpected panic attacks with physical symptoms',
  },
  {
    value: 'specific_phobia',
    label: 'Specific Phobia(s)',
    description: 'Intense, irrational fear of a specific object or situation',
  },
  {
    value: 'agoraphobia',
    label: 'Agoraphobia',
    description:
      'Fear of situations where escape might be difficult — crowds, open spaces, public transport',
  },
  // OCD & related
  {
    value: 'ocd',
    label: 'Obsessive-Compulsive Disorder (OCD)',
    description: 'Intrusive unwanted thoughts (obsessions) and repetitive behaviors (compulsions)',
  },
  {
    value: 'body_dysmorphia',
    label: 'Body Dysmorphic Disorder (BDD)',
    description:
      'Preoccupation with perceived flaws in physical appearance not observable by others',
  },
  {
    value: 'hoarding',
    label: 'Hoarding Disorder',
    description: 'Persistent difficulty discarding possessions regardless of their value',
  },
  {
    value: 'trichotillomania',
    label: 'Trichotillomania — Hair-Pulling Disorder',
    description: 'Recurrent urge to pull out hair resulting in noticeable hair loss',
  },
  {
    value: 'excoriation',
    label: 'Excoriation — Skin-Picking Disorder',
    description: 'Recurrent picking at skin resulting in lesions',
  },
  // Trauma & stressor-related
  {
    value: 'ptsd',
    label: 'Post-Traumatic Stress Disorder (PTSD)',
    description: 'Flashbacks, nightmares, and hypervigilance following traumatic events',
  },
  {
    value: 'cptsd',
    label: 'Complex PTSD (C-PTSD)',
    description:
      'PTSD symptoms plus difficulties with emotional regulation, self-concept, and relationships',
  },
  // Eating disorders
  {
    value: 'anorexia',
    label: 'Anorexia Nervosa',
    description: 'Restriction of food intake, intense fear of weight gain, distorted body image',
  },
  {
    value: 'bulimia',
    label: 'Bulimia Nervosa',
    description:
      'Binge eating followed by compensatory behaviors (purging, fasting, excessive exercise)',
  },
  {
    value: 'binge_eating',
    label: 'Binge Eating Disorder',
    description: 'Recurrent episodes of eating large amounts without compensatory behaviors',
  },
  {
    value: 'arfid',
    label: 'ARFID — Avoidant/Restrictive Food Intake Disorder',
    description:
      'Extremely picky eating or food avoidance not related to body image — often sensory-driven',
  },
  // Dissociative
  {
    value: 'did',
    label: 'Dissociative Identity Disorder (DID)',
    description: 'Presence of two or more distinct identity states with gaps in memory',
  },
  {
    value: 'depersonalization',
    label: 'Depersonalization / Derealization Disorder',
    description: 'Feeling detached from yourself or that the world around you is unreal',
  },
  // Schizophrenia spectrum
  {
    value: 'schizophrenia',
    label: 'Schizophrenia',
    description: 'Disruptions in thought processes, perceptions, emotions, and behavior',
  },
  {
    value: 'schizoaffective',
    label: 'Schizoaffective Disorder',
    description: 'Symptoms of schizophrenia combined with mood disorder episodes',
  },
  {
    value: 'other_mental_health',
    label: 'Other mental health condition',
    description: 'A condition not listed above',
  },
  { value: 'prefer_not_to_say', label: 'Prefer not to say' },
] as const;

// ─── 4. Personality Patterns (DSM-5 Cluster A/B/C) ─────────────────────────

export const personalityPatternOptions = [
  // Cluster A — Odd / Eccentric
  {
    value: 'paranoid_pd',
    label: 'Paranoid Personality',
    description: 'Pervasive distrust and suspicion of others — interpreting motives as malevolent',
  },
  {
    value: 'schizoid_pd',
    label: 'Schizoid Personality',
    description: 'Detachment from social relationships and limited emotional expression',
  },
  {
    value: 'schizotypal_pd',
    label: 'Schizotypal Personality',
    description: 'Eccentric behavior, unusual perceptual experiences, and social discomfort',
  },
  // Cluster B — Dramatic / Erratic
  {
    value: 'antisocial_pd',
    label: 'Antisocial Personality (ASPD)',
    description: 'Disregard for and violation of the rights of others — lack of remorse',
  },
  {
    value: 'borderline_pd',
    label: 'Borderline Personality (BPD)',
    description:
      'Instability in relationships, self-image, emotions, and impulsivity — fear of abandonment',
  },
  {
    value: 'histrionic_pd',
    label: 'Histrionic Personality (HPD)',
    description:
      'Excessive attention-seeking, dramatic behavior, and exaggerated emotional expression',
  },
  {
    value: 'narcissistic_pd',
    label: 'Narcissistic Personality (NPD)',
    description: 'Grandiosity, need for admiration, and lack of empathy for others',
  },
  // Cluster C — Anxious / Fearful
  {
    value: 'avoidant_pd',
    label: 'Avoidant Personality (AVPD)',
    description: 'Social inhibition, feelings of inadequacy, and extreme sensitivity to criticism',
  },
  {
    value: 'dependent_pd',
    label: 'Dependent Personality (DPD)',
    description: 'Excessive need to be taken care of — submissive, clingy, and fear of separation',
  },
  {
    value: 'ocpd',
    label: 'Obsessive-Compulsive Personality (OCPD)',
    description: 'Preoccupation with orderliness, perfectionism, and control — different from OCD',
  },
  {
    value: 'other_personality',
    label: 'Other personality pattern',
    description: 'A personality pattern not listed above',
  },
  { value: 'prefer_not_to_say', label: 'Prefer not to say' },
] as const;

// ─── 5. Neurological & Chronic Conditions ───────────────────────────────────

export const neurologicalChronicConditionOptions = [
  {
    value: 'epilepsy',
    label: 'Epilepsy / Seizure Disorder',
    description: 'Recurrent seizures caused by abnormal electrical activity in the brain',
  },
  {
    value: 'chronic_fatigue_me',
    label: 'Chronic Fatigue Syndrome / ME',
    description:
      'Extreme fatigue that doesn\u2019t improve with rest and worsens with physical/mental activity',
  },
  {
    value: 'fibromyalgia',
    label: 'Fibromyalgia',
    description: 'Widespread musculoskeletal pain with fatigue, sleep, and cognitive difficulties',
  },
  {
    value: 'chronic_pain',
    label: 'Chronic Pain Syndrome',
    description: 'Persistent pain lasting months or years beyond expected healing time',
  },
  {
    value: 'migraine',
    label: 'Chronic Migraine',
    description: 'Severe recurring headaches often with nausea, sensitivity to light and sound',
  },
  {
    value: 'tbi',
    label: 'Traumatic Brain Injury (TBI)',
    description: 'Brain damage from external force — can affect cognition, behavior, and emotions',
  },
  {
    value: 'ehlers_danlos',
    label: 'Ehlers-Danlos Syndrome (EDS)',
    description:
      'Connective tissue disorder causing joint hypermobility, skin fragility, and chronic pain',
  },
  {
    value: 'pots',
    label: 'POTS — Postural Orthostatic Tachycardia Syndrome',
    description:
      'Autonomic nervous system dysfunction causing rapid heartbeat and dizziness on standing',
  },
  {
    value: 'sleep_disorder',
    label: 'Sleep Disorder (Insomnia, Narcolepsy, Sleep Apnea)',
    description: 'Persistent difficulty falling/staying asleep, or excessive daytime sleepiness',
  },
  {
    value: 'none',
    label: 'None of these',
    description: 'No neurological or chronic conditions',
  },
  {
    value: 'other_neurological',
    label: 'Other neurological / chronic condition',
    description: 'A condition not listed above',
  },
  { value: 'prefer_not_to_say', label: 'Prefer not to say' },
] as const;

// ─── 6. Age of realization / diagnosis ──────────────────────────────────────

export const ageOfRealizationOptions = [
  { value: 'childhood', label: 'Childhood (before 13)' },
  { value: 'teen', label: 'Teen years (13–18)' },
  { value: 'young_adult', label: 'Young adult (19–30)' },
  { value: 'adult', label: 'Adult (31–50)' },
  { value: 'later_life', label: 'Later in life (50+)' },
  { value: 'still_exploring', label: 'Still exploring' },
  { value: 'prefer_not_to_say', label: 'Prefer not to say' },
] as const;

// ─── 7. Sensory profile ─────────────────────────────────────────────────────

export const sensoryProfileOptions = [
  { value: 'hyper_sound', label: 'Hypersensitive to sound' },
  { value: 'hypo_sound', label: 'Hyposensitive to sound' },
  { value: 'hyper_light', label: 'Hypersensitive to light' },
  { value: 'hypo_light', label: 'Hyposensitive to light' },
  { value: 'hyper_touch', label: 'Hypersensitive to touch / textures' },
  { value: 'hypo_touch', label: 'Hyposensitive to touch' },
  { value: 'hyper_smell', label: 'Hypersensitive to smells' },
  { value: 'hypo_smell', label: 'Hyposensitive to smells' },
  { value: 'hyper_taste', label: 'Hypersensitive to taste' },
  { value: 'hypo_taste', label: 'Hyposensitive to taste' },
  { value: 'seeks_stimulation', label: 'Seeks sensory stimulation' },
  { value: 'sensory_overwhelm', label: 'Sensory overwhelm / shutdown' },
  { value: 'none', label: 'No specific sensitivities' },
  { value: 'prefer_not_to_say', label: 'Prefer not to say' },
] as const;

// ─── 8. Cognitive strengths ─────────────────────────────────────────────────

export const cognitiveStrengthOptions = [
  { value: 'hyperfocus', label: 'Hyperfocus on interests' },
  { value: 'pattern_recognition', label: 'Pattern recognition' },
  { value: 'creative_thinking', label: 'Creative / divergent thinking' },
  { value: 'attention_to_detail', label: 'Attention to detail' },
  { value: 'systems_thinking', label: 'Systems thinking' },
  { value: 'big_picture', label: 'Big-picture thinking' },
  { value: 'visual_spatial', label: 'Visual-spatial skills' },
  { value: 'specialized_memory', label: 'Specialized / topic memory' },
  { value: 'problem_solving', label: 'Problem-solving' },
  { value: 'empathy', label: 'Empathy / emotional insight' },
  { value: 'passionate_curiosity', label: 'Passionate curiosity' },
  { value: 'directness_honesty', label: 'Honesty / directness' },
  { value: 'persistence', label: 'Persistence' },
  { value: 'none_unsure', label: 'None / unsure' },
  { value: 'prefer_not_to_say', label: 'Prefer not to say' },
] as const;

// ─── 9. Executive function challenges ───────────────────────────────────────

export const executiveFunctionChallengeOptions = [
  { value: 'planning_organization', label: 'Planning / organization' },
  { value: 'time_management', label: 'Time management / time blindness' },
  { value: 'task_initiation', label: 'Task initiation (getting started)' },
  { value: 'task_switching', label: 'Task switching / transitions' },
  { value: 'working_memory', label: 'Working memory' },
  { value: 'emotional_regulation', label: 'Emotional regulation' },
  { value: 'impulse_control', label: 'Impulse control' },
  { value: 'focus_attention', label: 'Focus / sustaining attention' },
  { value: 'prioritization', label: 'Prioritization' },
  { value: 'decision_fatigue', label: 'Decision-making / decision fatigue' },
  { value: 'self_motivation', label: 'Self-motivation' },
  { value: 'sleep_regulation', label: 'Sleep regulation' },
  { value: 'none', label: 'No significant challenges' },
  { value: 'prefer_not_to_say', label: 'Prefer not to say' },
] as const;

// ─── 10. Learning style ─────────────────────────────────────────────────────

export const learningStyleOptions = [
  { value: 'visual', label: 'Visual learner' },
  { value: 'auditory', label: 'Auditory learner' },
  { value: 'kinesthetic', label: 'Kinesthetic / hands-on' },
  { value: 'reading_writing', label: 'Reading & writing' },
  { value: 'multimodal', label: 'Multimodal (combination)' },
  { value: 'needs_repetition', label: 'Needs repetition' },
  { value: 'needs_structure', label: 'Learns best with structure' },
  { value: 'prefers_autonomy', label: 'Learns best with autonomy' },
  { value: 'prefer_not_to_say', label: 'Prefer not to say' },
] as const;

// ─── 11. Communication preference ───────────────────────────────────────────

export const communicationPreferenceOptions = [
  { value: 'face_to_face', label: 'Face-to-face verbal' },
  { value: 'written', label: 'Written (text / email / chat)' },
  { value: 'phone', label: 'Phone calls' },
  { value: 'video', label: 'Video calls' },
  { value: 'small_groups', label: 'Small groups only' },
  { value: 'one_on_one', label: 'One-on-one' },
  { value: 'visual_aids', label: 'With visual aids' },
  { value: 'async', label: 'Async over synchronous' },
  { value: 'prefer_not_to_say', label: 'Prefer not to say' },
] as const;

// ─── 12. Support & accommodations ───────────────────────────────────────────

export const supportAccommodationOptions = [
  { value: 'flexible_schedule', label: 'Flexible schedule / deadlines' },
  { value: 'written_instructions', label: 'Written instructions' },
  { value: 'quiet_workspace', label: 'Quiet workspace' },
  { value: 'regular_breaks', label: 'Regular breaks' },
  { value: 'body_doubling', label: 'Body-doubling / co-working' },
  { value: 'assistive_tech', label: 'Assistive technology' },
  { value: 'noise_cancelling', label: 'Noise-cancelling headphones' },
  { value: 'fidget_tools', label: 'Fidget tools / sensory items' },
  { value: 'structured_routines', label: 'Structured routines' },
  { value: 'clear_expectations', label: 'Clear expectations' },
  { value: 'visual_schedules', label: 'Visual schedules / checklists' },
  { value: 'therapy', label: 'Professional therapy / counseling' },
  { value: 'medication', label: 'Medication' },
  { value: 'peer_support', label: 'Peer support groups' },
  { value: 'coaching', label: 'ADHD / ASD coaching' },
  { value: 'family_support', label: 'Family / caregiver support' },
  { value: 'none', label: 'No specific support needed' },
  { value: 'prefer_not_to_say', label: 'Prefer not to say' },
] as const;

// ─── 13. Masking & burnout ──────────────────────────────────────────────────

export const maskingBurnoutOptions = [
  {
    value: 'frequently',
    label: 'Frequently — leads to burnout',
    description: 'Mask most of the time; often experience burnout',
  },
  { value: 'sometimes', label: 'Sometimes', description: 'Mask in certain settings' },
  { value: 'rarely', label: 'Rarely' },
  { value: 'never', label: 'Never' },
  { value: 'not_sure', label: 'Not sure what this means' },
  { value: 'prefer_not_to_say', label: 'Prefer not to say' },
] as const;

// ─── 14. Impact on daily life ───────────────────────────────────────────────

export const impactOnLifeOptions = [
  {
    value: 'significant',
    label: 'Significant impact',
    description: 'Need daily support to function',
  },
  {
    value: 'moderate',
    label: 'Moderate impact',
    description: 'Some support helps me thrive',
  },
  { value: 'mild', label: 'Mild impact', description: 'Manageable with awareness' },
  { value: 'minimal', label: 'Minimal / no noticeable impact' },
  { value: 'varies', label: 'Varies day to day' },
  { value: 'prefer_not_to_say', label: 'Prefer not to say' },
] as const;

// ─── Field Group ────────────────────────────────────────────────────────────

const ND_SKIP = ['no_neurotypical', 'prefer_not_to_say'];

export const neurodiversity: FieldGroup = {
  key: 'mental_health_neurodiversity',
  label: 'Mental Health & Neurodiversity',
  step: 8,
  description:
    'Neurodevelopmental conditions, mental health, personality patterns, neurological conditions, sensory profile, and support needs.',
  fields: [
    {
      order: 1,
      key: 'neurodiverseIdentity',
      label: 'Neurodivergent Identity',
      type: 'single_select',
      step: 8,
      isOnboarding: true,
      optional: true,
      options: [...neurodiverseIdentityOptions],
    },
    {
      order: 2,
      key: 'neurodevelopmentalConditions',
      label: 'Neurodevelopmental Conditions',
      type: 'multi_select',
      step: 8,
      isOnboarding: true,
      optional: true,
      options: [...neurodevelopmentalConditionOptions],
      conditionalOn: { field: 'neurodiverseIdentity', notIn: ND_SKIP },
    },
    {
      order: 3,
      key: 'mentalHealthConditions',
      label: 'Mental Health Conditions',
      type: 'multi_select',
      step: 8,
      isOnboarding: true,
      optional: true,
      options: [...mentalHealthConditionOptions],
      // Shown to everyone — neurotypical people have mental health conditions too
    },
    {
      order: 4,
      key: 'personalityPatterns',
      label: 'Personality Patterns',
      type: 'multi_select',
      step: 8,
      isOnboarding: true,
      optional: true,
      options: [...personalityPatternOptions],
      // Shown to everyone
    },
    {
      order: 5,
      key: 'neurologicalChronicConditions',
      label: 'Neurological & Chronic Conditions',
      type: 'multi_select',
      step: 8,
      isOnboarding: true,
      optional: true,
      options: [...neurologicalChronicConditionOptions],
      // Shown to everyone
    },
    {
      order: 6,
      key: 'ageOfRealization',
      label: 'Age of Realization',
      type: 'single_select',
      step: 8,
      isOnboarding: true,
      optional: true,
      options: [...ageOfRealizationOptions],
      conditionalOn: { field: 'neurodiverseIdentity', notIn: ND_SKIP },
    },
    {
      order: 7,
      key: 'sensoryProfile',
      label: 'Sensory Profile',
      type: 'multi_select',
      step: 8,
      isOnboarding: true,
      optional: true,
      options: [...sensoryProfileOptions],
      conditionalOn: { field: 'neurodiverseIdentity', notIn: ND_SKIP },
    },
    {
      order: 8,
      key: 'cognitiveStrengths',
      label: 'Cognitive Strengths',
      type: 'multi_select',
      step: 8,
      isOnboarding: true,
      optional: true,
      options: [...cognitiveStrengthOptions],
      conditionalOn: { field: 'neurodiverseIdentity', notIn: ND_SKIP },
    },
    {
      order: 9,
      key: 'executiveFunctionChallenges',
      label: 'Executive Function Challenges',
      type: 'multi_select',
      step: 8,
      isOnboarding: true,
      optional: true,
      options: [...executiveFunctionChallengeOptions],
      conditionalOn: { field: 'neurodiverseIdentity', notIn: ND_SKIP },
    },
    {
      order: 10,
      key: 'learningStyle',
      label: 'Learning Style',
      type: 'single_select',
      step: 8,
      isOnboarding: true,
      optional: true,
      options: [...learningStyleOptions],
    },
    {
      order: 11,
      key: 'communicationPreference',
      label: 'Communication Preference',
      type: 'single_select',
      step: 8,
      isOnboarding: true,
      optional: true,
      options: [...communicationPreferenceOptions],
    },
    {
      order: 12,
      key: 'supportAndAccommodations',
      label: 'Support & Accommodations',
      type: 'multi_select',
      step: 8,
      isOnboarding: true,
      optional: true,
      options: [...supportAccommodationOptions],
      conditionalOn: { field: 'neurodiverseIdentity', notIn: ND_SKIP },
    },
    {
      order: 13,
      key: 'maskingAndBurnout',
      label: 'Masking & Burnout',
      type: 'single_select',
      step: 8,
      isOnboarding: true,
      optional: true,
      options: [...maskingBurnoutOptions],
      conditionalOn: { field: 'neurodiverseIdentity', notIn: ND_SKIP },
    },
    {
      order: 14,
      key: 'impactOnLife',
      label: 'Impact on Daily Life',
      type: 'single_select',
      step: 8,
      isOnboarding: true,
      optional: true,
      options: [...impactOnLifeOptions],
      conditionalOn: { field: 'neurodiverseIdentity', notIn: ND_SKIP },
    },
  ],
};
